import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import FormatterLayout from "../components/FormatterLayout";
import AppLayout from "../components/AppLayout";
import ToolContent, { formatterContent } from "../toolContent";
import { load, save, remove } from "../storage";

const inputKey = (type) => `fmt:${type}:input`;
const optionsKey = (type) => `fmt:${type}:options`;

export default function Formatter({ entries }) {
  const { type } = useParams();
  const [input, setInput] = useState("");

  const [output, setOutput] = useState("");
  const [formatter, setFormatter] = useState(null);
  const [structCode, setStructCode] = useState("");
  const [formatterOptions, setFormatterOptions] = useState({});
  const [hasStructSupport, setHasStructSupport] = useState(false);
  // snapshot of the buffer taken right before the last in-place Format/Minify,
  // so "Revert" can restore it (on top of Monaco's own undo)
  const [revertBuffer, setRevertBuffer] = useState(null);
  // becomes true after the first Format, so options can live-update the buffer
  const hasFormattedRef = useRef(false);
  // the type whose state is fully loaded — guards persistence against the
  // brief window where `type` changed but state hasn't been re-seeded yet
  const loadedTypeRef = useRef(null);
  const sampleRef = useRef("");
  const defaultsRef = useRef({});

  // 🔹 Load formatter dynamically (restoring any saved buffer/options)
  useEffect(() => {
    loadedTypeRef.current = null;
    async function loadFormatter() {
      try {
        const module = await import(`../formatters/${type}.js`);
        setFormatter(module);
        setStructCode("");
        setOutput("");
        setRevertBuffer(null);
        hasFormattedRef.current = false;

        const sample = typeof module.sample === "string" ? module.sample : "";
        sampleRef.current = sample;

        const defaults = module.formatOptions
          ? Object.entries(module.formatOptions).reduce(
              (acc, [key, opt]) => ({ ...acc, [key]: opt.default }),
              {}
            )
          : {};
        defaultsRef.current = defaults;

        // Restore saved buffer + options, falling back to sample/defaults
        const savedInput = load(inputKey(type));
        setInput(savedInput != null ? savedInput : sample);
        setHasStructSupport(typeof module.struct === "function");

        let opts = defaults;
        const savedOptions = load(optionsKey(type));
        if (savedOptions) {
          try {
            opts = { ...defaults, ...JSON.parse(savedOptions) };
          } catch {}
        }
        setFormatterOptions(opts);

        loadedTypeRef.current = type;
      } catch (err) {
        console.error("Formatter not found:", type, err);
        setFormatter(null);
        setHasStructSupport(false);
      }
    }
    loadFormatter();
  }, [type]);

  // 🔹 Persist input + options per format (only once loaded for this type)
  useEffect(() => {
    if (loadedTypeRef.current !== type) return;
    save(inputKey(type), input);
  }, [input, type]);

  useEffect(() => {
    if (loadedTypeRef.current !== type) return;
    save(optionsKey(type), JSON.stringify(formatterOptions));
  }, [formatterOptions, type]);

  // 🔹 Reset this format back to its sample + default options
  const resetToSample = () => {
    remove(inputKey(type));
    remove(optionsKey(type));
    setFormatterOptions(defaultsRef.current);
    setInput(sampleRef.current);
    if (hasStructSupport) handleStructify(sampleRef.current);
  };

  // 🔹 Safe structify
  const handleStructify = async (text) => {
    if (formatter?.struct) {
      try {
        const fn = formatter.struct;
        const result = fn(text, formatterOptions);
        const structed = result instanceof Promise ? await result : result;
        setStructCode(String(structed ?? ""));
      } catch (err) {
        console.error("Struct generation failed:", err);
        setStructCode("");
      }
    } else {
      setStructCode("");
    }
  };

  // 🔹 Format handler — formats the buffer in place (single-pane model)
  const handleFormat = async () => {
    hasFormattedRef.current = true;
    try {
      const fn = formatter.format;
      const result = fn(input, formatterOptions);
      const formatted = String((result instanceof Promise ? await result : result) ?? "");
      setRevertBuffer(input); // snapshot the pre-format buffer for Revert
      setInput(formatted);
      if (formatter?.struct) await handleStructify(formatted);
    } catch (err) {
      console.error("Formatting error:", err);
    }
  };

  // 🔹 Restore the buffer captured before the last Format/Minify
  const handleRevert = () => {
    if (revertBuffer == null) return;
    const restored = revertBuffer;
    setInput(restored);
    setRevertBuffer(null);
    if (hasStructSupport) handleStructify(restored);
  };

  // 🔹 Live re-format the buffer when options change (only after first Format)
  useEffect(() => {
    if (!formatter?.format || !hasFormattedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const result = formatter.format(input, formatterOptions);
        const formatted = result instanceof Promise ? await result : result;
        if (!cancelled) setInput(String(formatted ?? ""));
      } catch (err) {
        console.error("Live formatting error:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // input is intentionally excluded — typing is handled by the editor itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatterOptions, formatter]);

  // 🔹 Live re-generate the Struct View when struct-group options change.
  // Struct options (tag style, field casing, Go time/uuid types) don't pass
  // through `format`, so this is what makes them reach the struct viewer.
  useEffect(() => {
    if (!hasStructSupport) return;
    handleStructify(input);
    // input is excluded — the editor's onChange already restructs on input edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatterOptions, hasStructSupport]);

  // 🔹 Minify handler — also transforms the buffer in place
  const handleMinify = async () => {
    if (formatter?.minify) {
      try {
        const fn = formatter.minify;
        const result = fn(input, formatterOptions);
        const minified = String((result instanceof Promise ? await result : result) ?? "");
        setRevertBuffer(input); // snapshot the pre-minify buffer for Revert
        setInput(minified);
        if (formatter?.struct) await handleStructify(minified);
      } catch (err) {
        console.error("Minify error:", err);
      }
    }
  };

  if (!formatter) {
    return (
      <div className="formatter-page">
        <h1>Unknown Formatter: {type}</h1>
      </div>
    );
  }

  const updateOption = (key, value) =>
    setFormatterOptions((prev) => ({ ...prev, [key]: value }));

  const clamp = (v, opt) => {
    let n = Number.isFinite(v) ? v : opt.default;
    if (typeof opt.min === "number") n = Math.max(opt.min, n);
    if (typeof opt.max === "number") n = Math.min(opt.max, n);
    return n;
  };

  const resetDefaults = () => {
    if (!formatter?.formatOptions) return;
    const defaults = Object.entries(formatter.formatOptions).reduce(
      (acc, [key, opt]) => ({ ...acc, [key]: opt.default }),
      {}
    );
    setFormatterOptions(defaults);
  };

  // Subsections shown in the options panel, in display order. An option's
  // `group` (defaulting to "output") decides which subsection it lands in.
  const OPTION_GROUPS = [
    { id: "output", title: "Output" },
    { id: "struct", title: "Struct View" },
  ];

  // Render a single option control (number stepper / checkbox / select).
  const renderField = (key, opt) => (
    <div key={key} className={`option-field option-${opt.type}`}>
      <span className="opt-text">
        <label htmlFor={`opt-${key}`}>{opt.label}</label>
        {opt.help && <span className="opt-help">{opt.help}</span>}
      </span>

      {opt.type === "number" && (
        <span className="stepper">
          <button
            type="button"
            aria-label={`Decrease ${opt.label}`}
            onClick={() =>
              updateOption(key, clamp((formatterOptions[key] ?? opt.default) - 1, opt))
            }
          >
            −
          </button>
          <input
            id={`opt-${key}`}
            type="number"
            min={opt.min}
            max={opt.max}
            value={formatterOptions[key]}
            onChange={(e) => updateOption(key, clamp(Number(e.target.value), opt))}
          />
          <button
            type="button"
            aria-label={`Increase ${opt.label}`}
            onClick={() =>
              updateOption(key, clamp((formatterOptions[key] ?? opt.default) + 1, opt))
            }
          >
            +
          </button>
        </span>
      )}

      {opt.type === "checkbox" && (
        <input
          id={`opt-${key}`}
          type="checkbox"
          className="switch"
          checked={!!formatterOptions[key]}
          onChange={(e) => updateOption(key, e.target.checked)}
        />
      )}

      {opt.type === "select" && (
        <select
          id={`opt-${key}`}
          value={formatterOptions[key]}
          onChange={(e) => updateOption(key, e.target.value)}
        >
          {opt.options.map((val) => (
            <option key={val} value={val}>
              {val}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  // Group the options by subsection, dropping struct-only groups when the
  // current formatter has no struct view, and any group that ends up empty.
  const groupedFields = OPTION_GROUPS.map((group) => {
    if (group.id === "struct" && !hasStructSupport) return null;
    const fields = Object.entries(formatter.formatOptions || {}).filter(
      ([, opt]) => (opt.group || "output") === group.id
    );
    return fields.length ? { ...group, fields } : null;
  }).filter(Boolean);
  // Only label subsections when there's more than one to distinguish.
  const showGroupTitles = groupedFields.length > 1;

  return (
    <AppLayout
      structCode={hasStructSupport ? structCode : ""}
      entries={entries}
      showRightSidebar={hasStructSupport}
      showLeftSidebar={
        formatter?.formatOptions && {
          name: "Formatting Options",
          component: (
            <div className="options-panel">
              {groupedFields.map((group) => (
                <section className="option-group" key={group.id}>
                  {showGroupTitles && (
                    <h4 className="option-group-title">{group.title}</h4>
                  )}
                  {group.fields.map(([key, opt]) => renderField(key, opt))}
                </section>
              ))}

              <button className="reset-defaults" onClick={resetDefaults}>
                ↺ Reset to defaults
              </button>
            </div>
          ),
        }
      }
    >
      <FormatterLayout
        title={type.toUpperCase()}
        kind="Formatter"
        input={input}
        setInput={setInput}
        output={output}
        setOutput={setOutput}
        onFormat={handleFormat}
        onMinify={formatter.minify ? handleMinify : null}
        onStructify={hasStructSupport ? handleStructify : null}
        onReset={resetToSample}
        onRevert={handleRevert}
        canRevert={revertBuffer != null}
        singlePane
        sourceLanguage={formatter.language || "json"}
        targetLanguage={formatter.language || "json"}
        hasStruct={hasStructSupport}
      />
      <ToolContent content={formatterContent(type, entries[type] || type.toUpperCase())} />
    </AppLayout>
  );
}
